import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FaFilter } from 'react-icons/fa'
import { FiInbox } from 'react-icons/fi'
import { VscClearAll } from 'react-icons/vsc'

/* form */
import Input from "../../shared/components/form/input"
import Button from "../../shared/components/Button/Button"
import Modal from '../../shared/modal/Modal'
import BudgetFilterModal from './BudgetFilterModal'

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'

import ListBudgetRow from './ListRow/ListBudgetRow'
import NewBudget from './NewBudget'
import DefaultConfigBudget from './DefaultConfig/DefaultConfigBudget'

const EMPTY_FILTERS = { status: 'all', paymentMethod: 'all', dateFrom: '', dateTo: '' }

function Budget() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [budgets, setBudgets] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalConfig, setModalConfig] = useState(false)

    const [search, setSearch] = useState("")
    const [load, setLoad] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [filters, setFilters] = useState(EMPTY_FILTERS)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionNew = checkPermission(myPermission, "budget_create")
    const myPermissionView = checkPermission(myPermission, "budget_view")
    const myPermissionEditSeller = checkPermission(myPermission, "budget_editSeller")
    const myPermissionEdit = checkPermission(myPermission, "budget_edit")
    const myPermissionDelete = checkPermission(myPermission, "budget_delete")
    const myPermissionConfig = checkPermission(myPermission, "budget_config")

    /* get all function */
    const getAll = () => {
        api.get(`/order/budget/getall`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`,
            },
        }).then((response) => {
            if (response) {
                setBudgets(response.data.budgets)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    /* use effect */
    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    /* ----------------------- Search function ---------------------------------- */
    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return budgets.filter((item) => {
            const matchSearch = item.seller?.name?.toLowerCase().includes(searchLowerCase)
            const matchStatus = filters.status === 'all' || (item.status || 'Criado') === filters.status
            const matchPayment = filters.paymentMethod === 'all' || item.paymentMethod?.value === filters.paymentMethod
            const itemDate = item.createdAt ? item.createdAt.slice(0, 10) : ''
            const matchDateFrom = !filters.dateFrom || itemDate >= filters.dateFrom
            const matchDateTo = !filters.dateTo || itemDate <= filters.dateTo
            return matchSearch && matchStatus && matchPayment && matchDateFrom && matchDateTo
        })
    }, [budgets, search, filters])

    function handleSearch(e) {
        setSearch(e.target.value)
    }

    const hasActiveFilter = filters.status !== 'all' || filters.paymentMethod !== 'all' || !!filters.dateFrom || !!filters.dateTo
    const handleFilterChange = (key, value) => setFilters((f) => ({ ...f, [key]: value }))
    const clearFilters = () => setFilters(EMPTY_FILTERS)

    if (!load) {
        return <div className="pg-loading">Carregando orçamentos...</div>
    }

    /* PAGE */
    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Todos os Orçamentos</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                            {myPermissionConfig && (
                                <Button variant="config" onClick={() => setModalConfig(true)}>Configurar</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por vendedor..."
                                    value={search}
                                    handleOnChange={handleSearch}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'orçamento' : 'orçamentos'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'orçamento encontrado' : 'orçamentos encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListBudgetRow
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermissionEdit={myPermissionEdit}
                        myPermissionDelete={myPermissionDelete}
                        myPermissionView={myPermissionView}
                        allowSellerSelect={myPermissionEditSeller}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum orçamento encontrado</p>
                        <p className="pg-empty-subtitle">
                            {budgets.length === 0
                                ? 'Ainda não há orçamentos cadastrados.'
                                : 'Tente ajustar a busca.'}
                        </p>
                    </div>
                )}
            </div>

            {modalFilter && (
                <BudgetFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                />
            )}

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                t={1}
                w={98}
                h={97}
                content={
                    <NewBudget btCancelAction={() => setModalNew(false)} handleOnSuccess={() => { setModalNew(false); getAll(); }} allowSellerSelect={myPermissionEditSeller} />
                }
            />

            <Modal
                modalStatus={modalConfig}
                changeStatus={setModalConfig}
                w={80}
                content={
                    <DefaultConfigBudget btCancelAction={() => setModalConfig(false)} />
                }
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar orçamentos</p>
        </div>
    )
}

export default Budget
